<template>
  <main>
    <form class="container" @submit.prevent="submit()">
      <al-notify v-if="errorToken" error name="error-msg">
        {{ $t(errorToken) }}
      </al-notify>
      <div class="row">
        <div class="col-full">
          <h1 class="heading-xl">
            {{ $t('addCard.heading') }}
          </h1>
        </div>
      </div>
      <div class="row">
        <div class="col-full">
          <al-card-input
            v-model="form.primaryAccountNumber"
            v-validate="{
              required: true,
              card_brand: true,
              credit_card: true
            }"
            :allowed-card-types="allowedCardTypes"
            :label="$t('addCard.cardNumberLabel')"
            name="card-number"
            :card-last-four.sync="cardMessage.lastFourDigits"
            :manual-error.sync="cardMessage.errorToken"
            data-vv-name="card-number"
            @focus="isButtonDisabled = false" />
        </div>
      </div>
      <div class="row">
        <div class="col-half">
          <al-input
            v-model="form.panExpiration"
            v-validate="{
              required: true,
              date_format: 'MM/yy',
              expiry_date: true
            }"
            :label="$t('addCard.expiryLabel')"
            :placeholder="$t('placeholder.cardExpiryDate')"
            autocomplete="off"
            type="tel"
            name="expiry"
            data-vv-name="expiry"
            data-test="expiry-date"
            @input="formatExpiry" />
        </div>
        <div class="col-half">
          <al-input
            v-model="form.cardSecurityCode"
            v-validate="{
              required: true,
              digits: 3
            }"
            :label="$t('placeholder.securityCode')"
            type="tel"
            name="cvc2"
            mask="###"
            autocomplete="off"
            data-vv-name="cvc2"
            data-test="cvc2" />
        </div>
      </div>

      <div class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <al-button
            v-analytics:click="$EVENTS.CONTINUE_BUTTON"
            :disabled="hasValidationErrors || isButtonDisabled"
            :loading="loading"
            :label="$t('button.next')"
            name="form-submit-button"
            uppercase />
        </div>
        <div class="col-full align-center">
          <back-button v-analytics:click="$EVENTS.CANCEL_BUTTON" :location="cancelBtnLocation">
            {{ $t(buttonContent) }}
          </back-button>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
import { AlButton, AlCardInput, AlInput, AlNotify } from '@mc-alberta/alcapone'
import nudetect from '@mc-alberta/nudetect'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import useBindUserData from '@/composables/useBindUserData'
import useConfirmModal from '@/composables/useConfirmModal'
import useFormValidation from '@/composables/useFormValidation'
import { ACTION, ADDRESS_FORMAT, LOADERS, ROUTES } from '@/constants'
import * as types from '@/store/mutation-types'
import formatExpiry from '@/utils/formatExpiry'
import { allowedCardTypes } from '@/validations/cardBrand.js'

export default {
  name: ROUTES.ADD_CARD,
  components: { AlButton, AlCardInput, AlInput, AlNotify, BackButton },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeaveConfirmModal(to, from, next)
  },
  setup(props, context) {
    const { errorToken, handleBind } = useBindUserData()
    const { beforeRouteLeaveConfirmModal } = useConfirmModal({ context })
    const { hasValidationErrors } = useFormValidation()

    return {
      errorToken,
      handleBind,
      hasValidationErrors,
      beforeRouteLeaveConfirmModal
    }
  },
  data() {
    const cancelBtnLocation = this.$store.getters.isLoggedIn
      ? { name: ROUTES.LIST_CARDS }
      : { name: ROUTES.ENTRY, params: { logout: true } }

    return {
      allowedCardTypes,
      isButtonDisabled: false,
      form: {
        primaryAccountNumber: '',
        panExpiration: '',
        cardSecurityCode: ''
      },
      retrySubmitCount: 0,
      cardMessage: {
        lastFourDigits: '',
        errorToken: ''
      },
      cancelBtnLocation
    }
  },
  computed: {
    ...mapGetters([
      'isDuplicateCard',
      'isLoggedIn',
      'locale',
      'newCardAddressFormat',
      'serviceErrorResponse'
    ]),
    ...mapState({
      loading: ({ actionIsPending }) => actionIsPending,
      action: ({ action }) => action,
      newCard: (state) => state.cards?.newCard
    }),
    // Changes after submit (enrollCard) is called
    isBillingAddressRequired() {
      return this.newCardAddressFormat !== ADDRESS_FORMAT.NONE
    },
    postEnrollRoute() {
      if (this.action === ACTION.REGISTER) {
        return this.isBillingAddressRequired ? ROUTES.REGISTER_BILLING : ROUTES.REGISTER_TERMS
      }
      return ROUTES.ADD_BILLING
    },
    cardErrorMessage() {
      const lastFour = this.$route.params.lastFourDigits || this.newCard?.panLastFour
      const message = this.$t('cards.duplicateCardError')
      return {
        lastFourDigits: String(lastFour),
        errorToken: message
      }
    },
    buttonContent() {
      return this.isLoggedIn ? 'button.viewAllCards' : 'button.goBack'
    }
  },
  mounted() {
    this.$nextTick(() => nudetect.beginBehaviouralMonitoring())
    this.$validator.validateAll()

    const error = this.$route.params.errorToken || ''

    if (error === 'cards.duplicateCardError') {
      this.cardMessage = this.cardErrorMessage
    } else {
      this.errorToken = error
    }
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions(['clearNewCardData', 'clearServiceLayerError', 'enrollCard', 'startLoader']),
    ...mapMutations({
      setActionIsPending: types.SET_ACTION_IS_PENDING
    }),
    async submit() {
      this.$analytics.track({
        event: this.$EVENTS.CONTINUE_BUTTON,
        meta: {
          hasBillingData: ADDRESS_FORMAT.NONE,
          hasShippingData: false,
          hasNationalId: false,
          marketBillingPreference: this.newCardAddressFormat,
          consumerNameRequested: false
        }
      })

      this.clearServiceLayerError()
      this.errorToken = null
      this.setActionIsPending(true)

      await this.enrollCard(this.form)
      nudetect.clearBehaviouralData()

      if (this.serviceErrorResponse()) return this.handleEnrollCardError()

      if (this.isDuplicateCard) {
        this.cardMessage = this.cardErrorMessage

        this.setActionIsPending(false)
        this.clearNewCardData()

        this.form.cardSecurityCode = ''
        this.form.panExpiration = ''

        this.$validator.reset()

        this.isButtonDisabled = true
      } else {
        this.postEnrollCard()
      }
    },
    async postEnrollCard() {
      if (
        this.isLoggedIn &&
        (this.$i18nData.isCountryRestricted(this.locale) || !this.isBillingAddressRequired)
      ) {
        await this.returningUserBindWithoutBilling()
      } else {
        this.$router.push({
          name: this.postEnrollRoute,
          params: { skipConfirmation: true, showStepTracker: this.action === ACTION.REGISTER }
        })
      }
    },
    async returningUserBindWithoutBilling() {
      this.startLoader(LOADERS.UPDATE_PROFILE)
      await this.handleBind()
    },
    formatExpiry(val) {
      this.$nextTick(() => {
        this.form.panExpiration = formatExpiry(val)
      })
    },
    handleEnrollCardError() {
      const { status, headers, data } = this.serviceErrorResponse()
      const { 'retry-after': retryAfter = 1000 } = headers

      if (status === 400) {
        nudetect.beginBehaviouralMonitoring()
        this.setActionIsPending(false)

        if (data?.status === 'INVALID_ISSUER') {
          this.cardMessage.errorToken = this.$t('addCard.invalidIssuerError')
        } else if (data?.status === 'INVALID_COUNTRY') {
          this.errorToken = this.$t('addCard.invalidCountryError')
        } else {
          this.errorToken = 'addCard.enrollmentError'
        }
      } else if ([503, 504].includes(status) && this.retrySubmitCount < 2) {
        nudetect.beginBehaviouralMonitoring()
        this.retrySubmitCount++
        setTimeout(this.submit, +retryAfter)
      } else {
        this.setActionIsPending(false)
        this.$router.push({ name: ROUTES.ERROR })
      }
    }
  }
}
</script>
